import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icons in react-leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Service area cities with coordinates
const serviceAreas = [
  {
    name: 'Redlands',
    coordinates: [34.0556, -117.1825],
    slug: 'redlands-ca',
    description: 'Professional mobile detailing in Redlands, CA'
  },
  {
    name: 'Loma Linda',
    coordinates: [34.0484, -117.2612],
    slug: 'loma-linda-ca',
    description: 'Mobile car detailing in Loma Linda'
  },
  {
    name: 'Yucaipa',
    coordinates: [34.0336, -117.0431],
    slug: 'yucaipa-ca',
    description: 'Auto detailing services in Yucaipa'
  },
  {
    name: 'Rancho Cucamonga',
    coordinates: [34.1064, -117.5931],
    slug: 'rancho-cucamonga-ca',
    description: 'Mobile detailing in Rancho Cucamonga'
  },
  {
    name: 'Riverside',
    coordinates: [33.9806, -117.3755],
    slug: 'riverside-ca',
    description: 'Car detailing throughout Riverside'
  },
  {
    name: 'San Bernardino',
    coordinates: [34.1083, -117.2898],
    slug: 'san-bernardino-ca',
    description: 'Professional detailing in San Bernardino'
  },
  {
    name: 'Ontario',
    coordinates: [34.0633, -117.6509],
    slug: 'ontario-ca',
    description: 'Mobile detailing near Ontario Airport'
  },
  {
    name: 'Corona',
    coordinates: [33.8753, -117.5664],
    slug: 'corona-ca',
    description: 'Auto detailing in Corona'
  },
  {
    name: 'Fontana',
    coordinates: [34.0922, -117.4350],
    slug: 'fontana-ca',
    description: 'Mobile car detailing in Fontana'
  }
];

// Custom popup component with navigation
const MarkerWithPopup = ({ area }) => {
  const navigate = useNavigate();

  return (
    <Marker position={area.coordinates}>
      <Popup>
        <div className="text-center">
          <h3 className="font-bold text-lg mb-2">{area.name}</h3>
          <p className="text-sm text-gray-600 mb-3">{area.description}</p>
          <button
            onClick={() => navigate(`/service-areas/${area.slug}`)}
            className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 transition-colors text-sm font-semibold"
          >
            View {area.name} Services
          </button>
        </div>
      </Popup>
    </Marker>
  );
};

// Component to set the map bounds to fit all markers
const FitBounds = ({ locations }) => {
  const map = useMap();

  useEffect(() => {
    if (locations.length > 0) {
      const bounds = L.latLngBounds(locations.map(loc => loc.coordinates));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [map, locations]);

  return null;
};

export const ServiceAreaMap = ({ height = '500px', showTitle = true }) => {
  // Center of Inland Empire (roughly between all cities)
  const center = [34.0522, -117.3644];

  return (
    <div className="w-full">
      {showTitle && (
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Service Area Map</h2>
          <p className="text-lg text-gray-600">
            Click any marker to view services for that city
          </p>
        </div>
      )}
      
      <div className="rounded-lg overflow-hidden shadow-xl border-2 border-gray-200">
        <MapContainer
          center={center}
          zoom={10}
          style={{ height: height, width: '100%' }}
          scrollWheelZoom={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {serviceAreas.map((area) => (
            <MarkerWithPopup key={area.slug} area={area} />
          ))}
          
          <FitBounds locations={serviceAreas} />
        </MapContainer>
      </div>

      {/* City List Below Map */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
        {serviceAreas.map((area) => (
          <div
            key={area.slug}
            className="flex items-center gap-2 text-sm"
          >
            <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
            <span className="font-semibold text-gray-700">{area.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ServiceAreaMap;
